import { useState } from "react"
import { toast } from "sonner"

import {
  useBorrarPresupuesto,
  useGuardarPresupuesto,
  usePresupuestos,
  usePuede,
} from "@/api/queries"
import type { Periodo, Presupuesto } from "@/api/types"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

const PERIODOS: Record<Periodo, string> = { day: "día", month: "mes" }

/** A partir de aquí conviene mirar, aunque todavía no se haya pasado. */
const PCT_AVISO = 80

type Borrador = { periodo: Periodo; limite: string; bloquear: boolean }

const VACIO: Borrador = { periodo: "month", limite: "", bloquear: false }

function Fila({
  p,
  puedeGestionar,
  onEditar,
  onQuitar,
}: {
  p: Presupuesto
  puedeGestionar: boolean
  onEditar: () => void
  onQuitar: () => void
}) {
  const cerca = p.pct_consumido >= PCT_AVISO
  return (
    <TableRow>
      <TableCell className="font-medium">{PERIODOS[p.period]}</TableCell>
      <TableCell className="text-right tabular-nums">{p.limit}</TableCell>
      <TableCell className="text-right tabular-nums">{p.gastado}</TableCell>
      <TableCell className="text-right tabular-nums">
        <span className={cerca ? "font-medium text-destructive" : undefined}>
          {p.pct_consumido.toFixed(1)} %
        </span>
      </TableCell>
      <TableCell>
        <Badge variant={p.on_exceed === "block" ? "destructive" : "secondary"}>
          {p.on_exceed === "block" ? "bloquea" : "avisa"}
        </Badge>
      </TableCell>
      {puedeGestionar && (
        <TableCell className="space-x-3 text-right">
          <button type="button" onClick={onEditar} className="text-sm hover:underline">
            Editar
          </button>
          <button
            type="button"
            onClick={onQuitar}
            className="text-sm text-destructive hover:underline"
          >
            Quitar
          </button>
        </TableCell>
      )}
    </TableRow>
  )
}

/**
 * Presupuestos de una aplicación.
 *
 * Vive en el detalle de la aplicación y no en una pantalla propia porque un presupuesto
 * no significa nada sin saber de quién es: la pregunta siempre es "¿cuánto puede gastar
 * *esta*?".
 */
export function BudgetsCard({ appId }: { appId: string }) {
  const presupuestos = usePresupuestos(appId)
  const guardar = useGuardarPresupuesto(appId)
  const borrar = useBorrarPresupuesto(appId)
  const puedeGestionar = usePuede("gestionar_presupuestos")

  const [borrador, setBorrador] = useState<Borrador | null>(null)
  const [editando, setEditando] = useState(false)
  const [porQuitar, setPorQuitar] = useState<Periodo | null>(null)

  const existentes = new Set((presupuestos.data ?? []).map((p) => p.period))

  function abrirNuevo() {
    // Se propone el periodo que aún no existe: solo hay dos, y ofrecer el que ya está
    // puesto lleva a sobrescribirlo sin querer.
    const libre: Periodo = existentes.has("month") ? "day" : "month"
    setBorrador({ ...VACIO, periodo: libre })
    setEditando(false)
  }

  function abrirEdicion(p: Presupuesto) {
    setBorrador({ periodo: p.period, limite: p.limit, bloquear: p.on_exceed === "block" })
    setEditando(true)
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    if (borrador === null) return
    try {
      await guardar.mutateAsync({
        periodo: borrador.periodo,
        datos: {
          limit: borrador.limite.trim(),
          on_exceed: borrador.bloquear ? "block" : "warn",
        },
      })
      toast.success("Presupuesto guardado")
      setBorrador(null)
    } catch {
      toast.error("No se pudo guardar el presupuesto")
    }
  }

  async function confirmarQuitar() {
    if (porQuitar === null) return
    try {
      await borrar.mutateAsync(porQuitar)
      toast.success("Presupuesto quitado", {
        description: "La aplicación queda sin límite para ese periodo.",
      })
    } catch {
      toast.error("No se pudo quitar el presupuesto")
    } finally {
      setPorQuitar(null)
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Presupuesto</CardTitle>
        {puedeGestionar && existentes.size < 2 && (
          <Button variant="outline" size="sm" onClick={abrirNuevo}>
            Poner presupuesto
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {presupuestos.isPending ? (
          <Skeleton className="h-24" />
        ) : presupuestos.isError ? (
          <p className="text-sm text-destructive">No se pudieron cargar los presupuestos.</p>
        ) : presupuestos.data.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Sin presupuesto: esta aplicación puede gastar sin tope.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Periodo</TableHead>
                  <TableHead className="text-right">Límite</TableHead>
                  <TableHead className="text-right">Gastado</TableHead>
                  <TableHead className="text-right">Consumido</TableHead>
                  <TableHead>Al pasarse</TableHead>
                  {puedeGestionar && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {presupuestos.data.map((p) => (
                  <Fila
                    key={p.period}
                    p={p}
                    puedeGestionar={puedeGestionar}
                    onEditar={() => abrirEdicion(p)}
                    onQuitar={() => setPorQuitar(p.period)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <p className="mt-4 text-xs text-muted-foreground">
          Un presupuesto acota <strong>esta aplicación entera</strong>, no a cada usuario
          suyo: el gateway no sabe qué es un usuario final, a propósito. El tope por persona
          lo pone quien sí lo sabe.
        </p>
      </CardContent>

      <Dialog open={borrador !== null} onOpenChange={(a) => !a && setBorrador(null)}>
        <DialogContent>
          <form onSubmit={enviar}>
            <DialogHeader>
              <DialogTitle>{editando ? "Editar presupuesto" : "Poner presupuesto"}</DialogTitle>
              <DialogDescription>
                El importe es en la moneda de la contabilidad, por periodo completo. El
                periodo se cuenta en UTC.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="periodo">Periodo</Label>
                <Select
                  value={borrador?.periodo ?? "month"}
                  onValueChange={(v) =>
                    setBorrador((b) => (b === null ? b : { ...b, periodo: v as Periodo }))
                  }
                  // Al editar no se cambia el periodo: sería crear otro presupuesto y
                  // dejar el viejo puesto, que es justo lo contrario de lo que parece.
                  disabled={editando}
                >
                  <SelectTrigger id="periodo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(["month", "day"] as const)
                      .filter((p) => editando || !existentes.has(p))
                      .map((p) => (
                        <SelectItem key={p} value={p}>
                          Por {PERIODOS[p]}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="limite">Límite</Label>
                <Input
                  id="limite"
                  inputMode="decimal"
                  placeholder="50.00"
                  value={borrador?.limite ?? ""}
                  onChange={(e) =>
                    setBorrador((b) => (b === null ? b : { ...b, limite: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="al-pasarse">Al pasarse</Label>
                <Select
                  value={borrador?.bloquear === true ? "block" : "warn"}
                  onValueChange={(v) =>
                    setBorrador((b) => (b === null ? b : { ...b, bloquear: v === "block" }))
                  }
                >
                  <SelectTrigger id="al-pasarse">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="warn">Avisar y dejar pasar</SelectItem>
                    <SelectItem value="block">Bloquear</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  <strong>Avisar</strong> es el arranque recomendado: cortar tráfico legítimo
                  por un fallo de medición es peor que un sobrecoste que se ve venir.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBorrador(null)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={guardar.isPending || (borrador?.limite ?? "").trim() === ""}
              >
                {guardar.isPending ? "Guardando…" : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={porQuitar !== null} onOpenChange={(a) => !a && setPorQuitar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar el presupuesto?</AlertDialogTitle>
            <AlertDialogDescription>
              La aplicación queda <strong>sin límite</strong> para ese periodo, no con límite
              cero. Si lo que querés es cortarla, pon un límite que bloquee.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarQuitar}>Quitar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
